import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";

/* ---------------- helpers ---------------- */

const toSafeId = (s) =>
  String(s ?? "field")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-_:./]/g, ""); // keep common safe chars

// Normalize many inputs (Date, ISO string, epoch ms) to yyyy-MM-dd for <input type="date">
const toDateInput = (val) => {
  if (val == null || val === "") return "";
  if (val instanceof Date) return val.toISOString().slice(0, 10);
  if (typeof val === "number") return new Date(val).toISOString().slice(0, 10);
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return val; // already yyyy-mm-dd
    const d = new Date(val);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
    return "";
  }
  try {
    const d = new Date(val);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
  } catch {}
  return "";
};

// "100x300" | "100 × 300" | "100" (square) -> {w,h} or null
const parseImageSize = (s) => {
  if (!s) return null;
  const cleaned = String(s).toLowerCase().replace(/[×*]/g, "x").replace(/\s+/g, "");
  const parts = cleaned.split("x").filter(Boolean);
  if (parts.length === 1) {
    const n = parseInt(parts[0], 10);
    if (Number.isFinite(n) && n > 0) return { w: n, h: n };
  } else if (parts.length >= 2) {
    const w = parseInt(parts[0], 10);
    const h = parseInt(parts[1], 10);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) return { w, h };
  }
  return null;
};

// Resolve a src for <img>. If it's a relative API path ("/api/uploads/..."),
// you can optionally pass uploadsBaseUrl="http://localhost:5000" to prefix the host.
const resolveImageSrc = (v, uploadsBaseUrl) => {
  if (!v) return "";
  if (v.startsWith("data:image/")) return v;
  if (/^https?:\/\//i.test(v) || v.startsWith("//")) return v;

  // relative path like "/api/uploads/..." or "/uploads/..."
  if (uploadsBaseUrl) {
    const base = uploadsBaseUrl.replace(/\/$/, "");
    const path = v.startsWith("/") ? v : `/${v}`;
    return `${base}${path}`;
  }
  return v; // same-origin
};

/** Read file -> cover-fit into exact (w,h) -> return dataURL (PNG) */
async function fileToSizedDataUrl(file, w, h, type = "image/png", quality = 0.92) {
  const img = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");

  // cover-fit scale and center-crop
  const scale = Math.max(w / img.width, h / img.height);
  const newW = img.width * scale;
  const newH = img.height * scale;
  const dx = (w - newW) / 2;
  const dy = (h - newH) / 2;

  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(img, dx, dy, newW, newH);

  return canvas.toDataURL(type, quality);
}

/* ---------------- subcomponent: Signature (draw/upload) ---------------- */

function SignatureField({
  id,
  label,
  required,
  hidden,
  disabled,
  imageSize,
  value,
  onChange,
}) {
  const { w, h } = imageSize ? parseImageSize(imageSize) ?? { w: 360, h: 160 } : { w: 360, h: 160 };
  const [mode, setMode] = useState("draw"); // "draw" | "upload"
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPtRef = useRef({ x: 0, y: 0 });

  // init canvas
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // white background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
  }, [w, h, mode]);

  const getPt = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const isTouch = e.type.startsWith("touch");
    const touch = isTouch ? (e.touches?.[0] || e.changedTouches?.[0]) : null;
    const clientX = isTouch ? touch.clientX : e.clientX;
    const clientY = isTouch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    if (disabled || mode !== "draw") return;
    drawingRef.current = true;
    lastPtRef.current = getPt(e);
    e.preventDefault();
  };
  const move = (e) => {
    if (!drawingRef.current || disabled || mode !== "draw") return;
    const ctx = canvasRef.current.getContext("2d");
    const pt = getPt(e);
    ctx.beginPath();
    ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPtRef.current = pt;
    e.preventDefault();
  };
  const end = (e) => {
    drawingRef.current = false;
    e.preventDefault();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // repaint white bg at logical size
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const tctx = tmp.getContext("2d");
    tctx.fillStyle = "#ffffff";
    tctx.fillRect(0, 0, w, h);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.drawImage(tmp, 0, 0, w, h, 0, 0, w, h);
  };

  const applyDrawing = () => {
    const src = canvasRef.current;
    if (!src) return;
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const tctx = tmp.getContext("2d");
    tctx.drawImage(src, 0, 0, src.width, src.height, 0, 0, w, h);
    const dataUrl = tmp.toDataURL("image/png", 0.92);
    onChange?.({ target: { id, type: "text", value: dataUrl } });
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please choose an image file.");
      return;
    }
    try {
      const dataUrl = await fileToSizedDataUrl(file, w, h, "image/png", 0.92);
      onChange?.({ target: { id, type: "text", value: dataUrl } });
    } catch {
      alert("Could not process image. Please try a different file.");
    }
  };

  const clearValue = () => onChange?.({ target: { id, type: "text", value: "" } });

  return (
    <div className={`flex flex-col gap-2 items-start w-full ${hidden ? "hidden" : ""}`}>
      <label htmlFor={id} className="form-label">
        <span className={`${required ? 'after:content-["*"] after:text-red-600/70' : ""}`}>{label}</span>
        <span className="ml-2 text-xs text-gray-500">({w}×{h}px)</span>
      </label>

      <div className="inline-flex rounded-md border overflow-hidden">
        <button
          type="button"
          className={`px-3 py-1.5 text-sm ${mode === "draw" ? "bg-[#0E86D4] text-white" : "bg-white text-gray-700"}`}
          onClick={() => setMode("draw")}
          disabled={disabled}
        >
          Draw
        </button>
        <button
          type="button"
          className={`px-3 py-1.5 text-sm border-l ${mode === "upload" ? "bg-[#0E86D4] text-white" : "bg-white text-gray-700"}`}
          onClick={() => setMode("upload")}
          disabled={disabled}
        >
          Upload
        </button>
      </div>

      {mode === "draw" && (
        <div className="flex flex-col gap-2">
          <div className="border rounded bg-white" style={{ width: w, height: h, touchAction: "none" }}>
            <canvas
              ref={canvasRef}
              onMouseDown={start}
              onMouseMove={move}
              onMouseUp={end}
              onMouseLeave={end}
              onTouchStart={start}
              onTouchMove={move}
              onTouchEnd={end}
              className="block"
              style={{ width: `${w}px`, height: `${h}px` }}
            />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={clearCanvas} disabled={disabled} className="px-3 py-1.5 text-sm bg-gray-100 border rounded hover:bg-gray-200">
              Clear Canvas
            </button>
            <button type="button" onClick={applyDrawing} disabled={disabled} className="px-3 py-1.5 text-sm bg-[#0E86D4] text-white rounded hover:bg-[#0d78bd]">
              Use Drawing
            </button>
          </div>
      </div>
      )}

      {mode === "upload" && (
        <div className="flex items-center gap-3">
          <input
            id={id}
            type="file"
            accept="image/*"
            capture="environment"
            disabled={disabled}
            onChange={handleFile}
            className="block text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-gray-200 file:text-gray-800 hover:file:bg-gray-300 disabled:opacity-50"
          />
        </div>
      )}

      <div className="border border-dashed border-gray-300 rounded p-2 bg-white" style={{ width: w + 16, maxWidth: "100%" }}>
        <div className="mx-auto bg-gray-50 flex items-center justify-center overflow-hidden" style={{ width: w, height: h }}>
          {value ? (
            <img src={value} alt={label || "signature"} style={{ width: w, height: h, objectFit: "cover" }} />
          ) : (
            <span className="text-xs text-gray-400">No signature (will be {w}×{h})</span>
          )}
        </div>
      </div>

      {value && (
        <button
          type="button"
          onClick={clearValue}
          disabled={disabled}
          className="px-3 py-1.5 text-sm bg-gray-100 border rounded hover:bg-gray-200"
        >
          Clear Value
        </button>
      )}

      {/* 🔌 To use a Signotec hardware pad later, replace this component with your SignotecPad and call:
          onChange?.({ target: { id, type: "text", value: dataUrlFromPad } });
      */}
    </div>
  );
}

/* ---------------- component ---------------- */

const TextField = ({
  label,
  onBlur,
  defaultValue,
  options,
  id: idProp,
  // NEW optional props:
  isImage,          // boolean to force image preview (e.g., field.isImage)
  imageSize,        // "100x300" or "100"
  uploadsBaseUrl,   // e.g., "http://localhost:5000" (backend origin)
  ...rest
}) => {
  const { required, type, hidden, value, disabled } = rest;
 
  const id = idProp || toSafeId(label);

  // Normalize options for dropdowns:
  const fieldOptions = Array.isArray(options)
    ? options.map((opt) => (typeof opt === "string" ? opt : opt?.fieldValue))
    : [];

  /* ---------- SIGNATURE ---------- */
  if (type === "signature") {
    // Editable => interactive signature
    if (!disabled) {
      return (
        <SignatureField
          id={id}
          label={label}
          required={required}
          hidden={hidden}
          disabled={disabled}
          imageSize={imageSize}
          value={value ?? defaultValue ?? ""}
          onChange={rest.onChange}
        />
      );
    }

    // Read-only => preview (value can be base64 or stored path)
    const valStr = value ?? defaultValue ?? "";
    const src = resolveImageSrc(
      valStr,
      uploadsBaseUrl || `${process.env.REACT_APP_BASE_URL || ""}/Uploads`
    );
    const dims = parseImageSize(imageSize);
    const w = dims?.w ?? 360;
    const h = dims?.h ?? 160;

    return (
      <div className={`flex flex-col space-y-2 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
          <span className="ml-2 text-xs text-gray-500">({w}×{h}px)</span>
        </label>
        <div className="border border-dashed border-gray-300 rounded bg-white p-2" style={{ width: w + 16, maxWidth: "100%" }}>
          <div className="mx-auto bg-gray-50 flex items-center justify-center overflow-hidden" style={{ width: w, height: h }}>
            {src ? (
              <img
                src={src}
                alt={label || "signature"}
                style={{ width: w, height: h, objectFit: "cover" }}
              />
            ) : (
              <span className="text-xs text-gray-400">No signature</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ---------- IMAGE PREVIEW (read-only display) ---------- */
  if (isImage) {
    const valStr = value ?? defaultValue ?? "";
    const src = resolveImageSrc(
      valStr,
      uploadsBaseUrl || `${process.env.REACT_APP_BASE_URL || ""}/Uploads`
    );
    const dims = parseImageSize(imageSize);
    // sensible fallback box if no size provided
    const w = dims?.w ?? 220;
    const h = dims?.h ?? 140;

    return (
      <div className={`flex flex-col space-y-2 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
          {dims ? <span className="ml-2 text-xs text-gray-500">({w}×{h}px)</span> : null}
        </label>

        <div className="border border-dashed border-gray-300 rounded bg-white p-2" style={{ width: w + 16, maxWidth: "100%" }}>
          <div className="mx-auto bg-gray-50 flex items-center justify-center overflow-hidden" style={{ width: w, height: h }}>
            {src ? (
              <img
                src={src}
                alt={label || "image"}
                style={{ width: w, height: h, objectFit: "cover" }}
              />
            ) : (
              <span className="text-xs text-gray-400">No image</span>
            )}
          </div>
        </div>

        {src && (
          <a
            href={src}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 underline print:hidden"
          >
            Open full image
          </a>
        )}
      </div>
    );
  }

  /* ---------- RADIO ---------- */
  if (type === "radio") {
    return (
      <div className={`flex space-x-2 items-center w-36 ${hidden ? "hidden" : ""}`}>
        <input type="radio" id={id} className="w-6 h-6" autoComplete="off" {...rest} />
        <label htmlFor={id} className="form-label flex w-full">
          <span className="block">{label}</span>
        </label>
      </div>
    );
  }

  /* ---------- CHECKBOX (toggle style) ---------- */
  if (type === "checkbox") {
    return (
      <div className={`flex flex-col items-start w-full space-y-1 ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <label className="relative inline-flex items-center cursor-pointer">
          <input id={id} type="checkbox" {...rest} className="sr-only peer" />
          <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#0E86D4]" />
        </label>
      </div>
    );
  }

  /* ---------- ACCOUNT NUMBER (masked) ---------- */
  if (type === "accountnumber") {
    return (
      <div className={`flex flex-col items-start w-full space-y-1 ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <InputMask
          mask="999/999999/*"
          id={id}
          className="form-input"
          maskChar=""
          onBlur={onBlur}
          {...rest}
        />
      </div>
    );
  }

  /* ---------- GHANA CARD (masked) ---------- */
  if (type === "ghanacard") {
    return (
      <div className={`flex flex-col items-start w-full space-y-1 ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <InputMask
          mask="aaa-999999999-*"
          id={id}
          className="form-input"
          maskChar=""
          onBlur={onBlur}
          {...rest}
        />
      </div>
    );
  }

  /* ---------- DROPDOWN ---------- */
  if (type === "dropdown") {
    return (
      <div className={`flex flex-col items-start w-full space-y-1 ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <select id={id} name={id} className="form-input" {...rest}>
          <option key={-1} value="" className="text-slate-300">
            select an option
          </option>
          {fieldOptions?.map(
            (opt, index) =>
              opt != null && (
                <option key={index} value={opt}>
                  {opt}
                </option>
              )
          )}
        </select>
      </div>
    );
  }

  /* ---------- NUMBER ---------- */
  if (type === "number") {
    return (
      <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <input
          id={id}
          type="number"
          className="form-input"
          autoComplete="off"
          step={0.01}
          min={1}
          onBlur={onBlur}
          defaultValue={defaultValue}
          {...rest}
        />
      </div>
    );
  }

  /* ---------- DATE ---------- */
  if (type === "date") {
    const { value, min, max, ...others } = rest;
    return (
      <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <input
          id={id}
          type="date"
          className="form-input"
          autoComplete="off"
          onBlur={onBlur}
          defaultValue={toDateInput(defaultValue)}
          value={value !== undefined ? toDateInput(value) : undefined}
          min={min !== undefined ? toDateInput(min) : undefined}
          max={max !== undefined ? toDateInput(max) : undefined}
          {...others}
        />
      </div>
    );
  }

  /* ---------- TEXTAREA ---------- */
  if (type === "textarea") {
    return (
      <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
        <label htmlFor={id} className="form-label">
          <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
            {label}
          </span>
        </label>
        <textarea
          id={id}
          rows="4"
          cols="50"
          className="form-txtarea"
          autoComplete="off"
          onBlur={onBlur}
          defaultValue={defaultValue}
          {...rest}
        />
      </div>
    );
  }

  /* ---------- DEFAULT INPUT (text, email, etc.) ---------- */
  return (
    <div className={`flex flex-col space-y-1 items-start w-full ${hidden ? "hidden" : ""}`}>
      <label htmlFor={id} className="form-label">
        <span className={required ? 'after:content-["*"] after:text-red-600/70' : ""}>
          {label}
        </span>
      </label>
      <input
        id={id}
        className="form-input"
        autoComplete="off"
        onBlur={onBlur}
        defaultValue={defaultValue}
        {...rest}
      />
    </div>
  );
};

export default TextField;
